import { Button, ComponentLoader, FormGroup, FormRadioGroup, H3, Input } from '@snsw/react-component-library';
import { useDocumentWidth } from 'components/Utils/useDocumentWidth';
import React, { ReactNode, useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { createGlobalStyle } from 'styled-components';
import styled, { css } from 'styled-components/macro';
import FieldError from '../FieldError';
import styles from './SmartOrganisationSearch.module.scss';
import SmartOrganisationSearchService from './SmartOrganisationSearchService';
import type {
  Organisation,
  OrganisationDetail,
  OrganisationSearchAttributeConfig,
  OrganisationSearchResult,
  OrganisationSearchResultWithDetail,
} from './types';

const Title = styled.div<{ withSelector: boolean }>`
  font-size: 1rem;
  font-weight: 500;
  ${(props) =>
    props.withSelector
      ? css`
          margin-top: 1.5rem;
        `
      : css`
          margin-top: 1rem;
        `}
`;

const CustomFormRadioGroup = styled(FormRadioGroup)`
  min-height: calc(72px);
  margin-left: 0;
  margin-right: 0;
`;

const FakeHyperlink = styled.span`
  color: #2e5299;
  text-decoration: underline;
`;

const ManualOrgContainer = styled.div`
  margin-top: 1.5rem;
`;

const RegNumberInput = styled(Input)`
  max-width: 246px;
`;

const OrgNameInput = styled(Input)`
  max-width: 416px;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const GlobalStyle = createGlobalStyle`
  .main-abnAcn-container {
    .abnAcnSelect__control {
      height: 48px;
      margin-top: 4px;
      border: 2px solid #646974;
    }
    .with-error {
      .abnAcnSelect__control {
        border: 2px solid #b81237;
      }
    }
  }
`;

let timer;

function debounce(func, timeout = 300) {
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

const OPTION_1 = 'ABN or ACN';
const OPTION_2 = 'Y/INC or Other';
const options = [OPTION_1, OPTION_2];

const DEFAULT_ORGANISATION: Organisation = {
  key: 0,
  businessName: '',
};

export interface SmartOrganisationSearchProps {
  id: string;
  name: string;
  title?: string;
  subtitle?: string;
  helpMessage?: ReactNode;
  hasError?: boolean;
  errorMessage?: string;
  configManualRequired?: OrganisationSearchAttributeConfig<boolean>;
  configManualMaxLength?: OrganisationSearchAttributeConfig<number>;
  defaultValue?: OrganisationSearchResultWithDetail;

  authorizationToken?: string;
  apiRoute?: string;
  baseURL?: string;
  enableManualAbr: boolean;
  enableManualYinc: boolean;
  disableSelection: boolean;
  enableOrganisationNameUppercase: boolean;

  onChange?: (value: OrganisationSearchResult) => void;
  onTypeChange?: (searchType: string) => void;
  onDetailChange?: (detail: OrganisationDetail) => void;
  onManualValidationChange?: (hasError: boolean) => void;
  onClear?: () => void;

  className?: string;
}

const NO_OP = () => {};

export const SmartOrganisationSearch = ({
  id,
  name,
  hasError,
  errorMessage,
  configManualRequired,
  configManualMaxLength,
  title,
  subtitle,
  helpMessage,
  className,
  defaultValue,
  onManualValidationChange,
  onChange,
  onTypeChange,
  onDetailChange,
  onClear,
  apiRoute,
  baseURL,
  authorizationToken,
  enableManualAbr,
  enableManualYinc,
  disableSelection,
  enableOrganisationNameUppercase,
}: SmartOrganisationSearchProps) => {
  const getInitialSelectedSearch = () => defaultValue?.searchType ?? (disableSelection ? OPTION_1 : undefined);
  const [selectedSearch, setSelectedSearch] = useState(getInitialSelectedSearch());
  const [searchTerm, setSearchTerm] = useState(defaultValue?.searchTerm);
  const [selectedAbnAcnOption, setSelectedAbnAcnOption] = useState(defaultValue?.selectedAbnAcnOption);
  const [manualAbnAcn, setManualAbnAcn] = useState(
    typeof defaultValue?.manualAbnAcn === 'boolean' ? defaultValue?.manualAbnAcn : false
  );
  const [selectedYincOption, setSelectedYincOption] = useState(defaultValue?.selectedYincOption);
  const [manualYinc, setManualYinc] = useState(
    typeof defaultValue?.manualYinc === 'boolean' ? defaultValue?.manualYinc : false
  );
  const [fetchingDetail, setFetchingDetail] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState(defaultValue?.registrationNumber);
  const [organisationName, setOrganisationName] = useState(defaultValue?.organisationName);
  const [detail, setDetail] = useState(defaultValue?.detail);
  const [detailNotFound, setDetailNotFound] = useState(
    typeof defaultValue?.detailNotFound === 'undefined' ? false : defaultValue?.detailNotFound
  );
  const [hasErrorRegNumber, setHasErrorRegNumber] = useState(false);
  const [hasErrorOrgName, setHasErrorOrgName] = useState(false);

  const configMaxLength = selectedSearch == 'ABN or ACN' ? configManualMaxLength?.abn : configManualMaxLength?.yinc;
  const defaultManualRegNumberMaxLength = 10;
  const defaultManualOrgNameMaxLength = 100;

  const vw = useDocumentWidth();

  useEffect(() => {
    if (detail && onDetailChange) {
      onDetailChange({ ...detail });
    }
  }, [detail, onDetailChange]);

  useEffect(() => {
    if (onTypeChange && selectedSearch) {
      onTypeChange(selectedSearch);
    }

    //check manual validation when change type
    const configRequired = selectedSearch === 'ABN or ACN' ? configManualRequired?.abn : configManualRequired?.yinc;
    const hasFieldRegError = !!(configRequired?.reg && !registrationNumber?.length);
    const hasFieldOrgError = !!(configRequired?.org && !organisationName?.length);

    setHasErrorRegNumber(hasFieldRegError);
    setHasErrorOrgName(hasFieldOrgError);
    onManualValidationChange && onManualValidationChange(hasFieldRegError || hasFieldOrgError);
  }, [selectedSearch]);

  const clearResult = () => {
    setSelectedSearch(getInitialSelectedSearch());
    setSearchTerm(undefined);
    setSelectedAbnAcnOption(undefined);
    setSelectedYincOption(undefined);
    setFetchingDetail(false);
    setDetailNotFound(false);
    setDetail(undefined);
  };

  useEffect(() => {
    const getYincDetail = async () => {
      if (!selectedYincOption) return;

      if (selectedYincOption.key === -1) {
        setDetailNotFound(true);
        return;
      }

      setFetchingDetail(true);
      setDetailNotFound(false);

      const detail = await SmartOrganisationSearchService.getYincDetail(
        authorizationToken,
        `${baseURL}${apiRoute}`,
        selectedYincOption.key,
        selectedYincOption.businessName
      );

      setFetchingDetail(false);
      setDetail(detail);
    };

    getYincDetail().then();
  }, [selectedYincOption]);

  useEffect(() => {
    const getAbnAcnDetail = async () => {
      if (!selectedAbnAcnOption?.key) return;

      if (selectedAbnAcnOption.key === -1) {
        setDetailNotFound(true);
        return;
      }

      setFetchingDetail(true);
      setDetailNotFound(false);

      const detail = await SmartOrganisationSearchService.getAbnAcnDetail(
        authorizationToken,
        `${baseURL}${apiRoute}`,
        selectedAbnAcnOption.key
      );
      setDetail(detail);
      setFetchingDetail(false);
    };

    getAbnAcnDetail().then();
  }, [selectedAbnAcnOption]);

  useEffect(() => {
    if (!onChange) return;

    onChange({
      searchType: selectedSearch,
      searchTerm,
      selectedAbnAcnOption,
      selectedYincOption,
      detailNotFound,
      manualAbnAcn,
      manualYinc,
      registrationNumber,
      organisationName,
    });
  }, [
    selectedSearch,
    searchTerm,
    selectedAbnAcnOption,
    selectedYincOption,
    detailNotFound,
    manualAbnAcn,
    manualYinc,
    registrationNumber,
    organisationName,
  ]);

  const handleAbnAcnOption = async (org: Organisation) => {
    if (!org.key) {
      setSelectedAbnAcnOption(undefined);
      setManualAbnAcn(true);
    } else {
      setSelectedAbnAcnOption(org);
    }
  };

  const handleYincOption = async (org: Organisation) => {
    if (!org.key) {
      setSelectedYincOption(undefined);
      setManualYinc(true);
    } else {
      setSelectedYincOption(org);
    }
  };

  const renderDefaultOption = () => (
    <span>
      Unable to find organisation? <FakeHyperlink>Enter manually</FakeHyperlink>
    </span>
  );

  const renderManualInputs = (configRequired) => {
    return (
      <>
        <ManualOrgContainer>
          <FormGroup
            hasError={hasErrorRegNumber}
            id="registrationNumber"
            label={`Registration number${configRequired?.reg ? '' : ' (optional)'}`}
            errorMessage="Registration number is required"
          >
            <RegNumberInput
              value={registrationNumber}
              maxLength={configMaxLength?.reg ? configMaxLength?.reg : defaultManualRegNumberMaxLength}
              onChange={(e) => {
                setRegistrationNumber(e.target.value);
                const hasFieldError = !!(configRequired?.reg && e.target.value.length <= 0);
                setHasErrorRegNumber(hasFieldError);
                onManualValidationChange && onManualValidationChange(hasFieldError);
              }}
            />
          </FormGroup>
        </ManualOrgContainer>
        <ManualOrgContainer>
          <FormGroup
            hasError={hasErrorOrgName}
            id="organisationName"
            label={`Organisation name${configRequired?.org ? '' : ' (optional)'}`}
            errorMessage="Organisation name is required"
          >
            <OrgNameInput
              value={organisationName}
              maxLength={configMaxLength?.org ? configMaxLength?.org : defaultManualOrgNameMaxLength}
              onChange={(e) => {
                setOrganisationName(enableOrganisationNameUppercase ? e.target.value.toUpperCase() : e.target.value);
                const hasFieldError = !!(configRequired?.org && e.target.value.length <= 0);
                setHasErrorOrgName(hasFieldError);
                onManualValidationChange && onManualValidationChange(hasFieldError);
              }}
            />
          </FormGroup>
        </ManualOrgContainer>
        <ManualOrgContainer>
          <Button
            variant="link"
            onClick={() => {
              setManualAbnAcn(false);
              setManualYinc(false);
              setRegistrationNumber(undefined);
              setOrganisationName(undefined);
            }}
          >
            Use lookup instead
          </Button>
        </ManualOrgContainer>
      </>
    );
  };

  const withSelector = typeof disableSelection !== 'boolean' || disableSelection === false;

  return (
    <div className={`${className} main-abnAcn-container`}>
      <GlobalStyle />
      {!fetchingDetail && detail ? (
        <>
          <div className={styles.detail}>
            <div className={styles.titleLine}>
              <h3 className={styles.titleLeft}>{detail.entityName}</h3>
              <div className={styles.titleRight}>
                <Button
                  variant={'link'}
                  onClick={(e) => {
                    e.preventDefault();
                    clearResult();
                    onClear && onClear();
                  }}
                >
                  Change
                </Button>
              </div>
            </div>
            {detail.abn ? (
              <div className={styles.detailLine}>
                <div className={styles.detailLeft}>ABN</div>
                <div className={styles.detailRight}>{detail.abn}</div>
              </div>
            ) : null}
            {detail.acn ? (
              <div className={styles.detailLine}>
                <div className={styles.detailLeft}>ACN</div>
                <div className={styles.detailRight}>{detail.acn}</div>
              </div>
            ) : null}
            {detail.yincOther ? (
              <div className={styles.detailLine}>
                <div className={styles.detailLeft}>Registration name or number</div>
                <div className={styles.detailRight}>{detail.yincOther}</div>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <>
          {typeof title === 'string' ? <H3>{title}</H3> : title}
          {withSelector && (
            <CustomFormRadioGroup
              id={`radio-${id}`}
              name={`radio-${name}`}
              legend={'Search by'}
              helpMessage={helpMessage}
              vertical={vw < 600}
              options={options.map((o) => ({
                value: o,
                label: o,
              }))}
              onChange={(v) => {
                setDetail(undefined);
                setFetchingDetail(false);
                setDetailNotFound(true);
                setSelectedSearch(v);
              }}
              value={selectedSearch}
              hasError={hasError}
            />
          )}
          {selectedSearch === OPTION_1 ? (
            manualAbnAcn ? (
              renderManualInputs(configManualRequired?.abn)
            ) : (
              <>
                <Title withSelector={withSelector}>{subtitle}</Title>
                <AsyncSelect<Organisation>
                  hasError={hasError}
                  id={`asyncSelect1-${id}`}
                  name={`asyncSelect1-${name}`}
                  classNamePrefix={`abnAcnSelect`}
                  className={`${hasError ? 'with-error' : ''}`}
                  cacheOptions
                  loadOptions={(inputValue) =>
                    new Promise((res) =>
                      debounce(
                        () =>
                          res(
                            SmartOrganisationSearchService.searchAbnAcn(
                              authorizationToken,
                              `${baseURL}${apiRoute}`,
                              inputValue,
                              enableManualAbr
                            )
                          ),
                        1500
                      )()
                    )
                  }
                  defaultOptions={enableManualAbr ? [DEFAULT_ORGANISATION] : []}
                  onInputChange={setSearchTerm}
                  formatOptionLabel={(e) => (!e.key ? renderDefaultOption() : e?.businessName)}
                  value={selectedAbnAcnOption}
                  onChange={handleAbnAcnOption}
                />
              </>
            )
          ) : null}
          {selectedSearch === OPTION_2 ? (
            manualYinc ? (
              renderManualInputs(configManualRequired?.yinc)
            ) : (
              <>
                <Title withSelector={withSelector}>Registration name or number</Title>
                <AsyncSelect<Organisation>
                  hasError={hasError}
                  id={`asyncSelect2-${id}`}
                  name={`asyncSelect2-${name}`}
                  classNamePrefix={`abnAcnSelect`}
                  className={`${hasError ? 'with-error' : ''}`}
                  cacheOptions
                  loadOptions={(inputValue) =>
                    new Promise((res) =>
                      debounce(
                        () =>
                          res(
                            SmartOrganisationSearchService.searchYinc(
                              authorizationToken,
                              `${baseURL}${apiRoute}`,
                              inputValue,
                              enableManualYinc
                            )
                          ),
                        1500
                      )()
                    )
                  }
                  defaultOptions={enableManualYinc ? [DEFAULT_ORGANISATION] : []}
                  onInputChange={setSearchTerm}
                  formatOptionLabel={(e) => (!e.key ? renderDefaultOption() : e?.businessName)}
                  value={selectedYincOption}
                  onChange={handleYincOption}
                />
              </>
            )
          ) : null}
          {hasError && errorMessage && <FieldError message={errorMessage} />}
          {fetchingDetail && <ComponentLoader />}
        </>
      )}
    </div>
  );
};

SmartOrganisationSearch.defaultProps = {
  title: 'Registration type',
  subtitle: 'ABN, ACN or business name',

  apiRoute: '/OrgSearchApiV2',
  baseURL: 'http://localhost:3000',
  enableManualAbr: false,
  enableManualYinc: false,
  disableSelection: false,
  enableOrganisationNameUppercase: false,

  onChange: NO_OP,
  onTypeChange: NO_OP,
  onDetailChange: NO_OP,
  onManualValidationChange: NO_OP,
  onClear: NO_OP,
};
